<template>
  <!--
    Displays the user-dependant texts within a template.
  -->
  <Portlet
    :title="portletTitle"
    class="userDependentImageList"
    icon="clone"
  >
    <LoadingPlaceholder v-if="loading" />
    <template v-else>
      <ThemeElementTabs
        :theme-id="themeId"
      />

      <Grid
        :ref="kgm_ref"
        :style="{height: 'auto'}"
        :data-items="kgm_computedGridItems(userDependentImageElements)"
        :columns="kgm_responsiveColumns()"
        :filterable="true"
        :filter="kgm_filter"
        :pageable="kgm_pagable"
        :page-size="kgm_take"
        :skip="kgm_skip"
        :take="kgm_take"
        :total="kgm_allGridItems(userDependentImageElements)"
        :sortable="{
          allowUnsort: kgm_allowUnsort,
          mode: kgm_sortMode
        }"
        :sort="kgm_sort"
        selected-field="selected"
        @rowclick="kgm_gridOnRowClick"
        @filterchange="kgm_gridFilterChange"
        @pagechange="kgm_gridPageChange"
        @sortchange="kgm_gridSortChange"
      >
        <div
          slot="nameFilter"
          slot-scope="{props, methods}"
          class="input-group"
        >
          <input
            ref="nameFilterInput"
            :value="props.value"
            type="text"
            class="form-control mr-2 rounded-right"
            placeholder="please enter a source name"
            @input="(event) => {methods.change({operator: 'contains', field: props.field, value: event.target.value, syntheticEvent: event});}"
          >
          <div
            v-if="props.value"
            class="input-group-append"
          >
            <button
              class="btn btn-light border rounded"
              @click="kgm_resetInput('nameFilterInput', (event) => {methods.change({operator: '', field: '', value: '', syntheticEvent: event});})"
            >
              <font-awesome-icon
                class="gray"
                icon="times"
              />
            </button>
          </div>
        </div>
        <template
          slot="previewTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <Preview
              :edit="false"
              :preview-only="true"
              :element="props.dataItem"
              style="width:5rem; float:left;margin-right: 1rem;overflow:hidden;"
              tag="rect"
            />
          </td>
        </template>
        <template
          slot="optionsTemplate"
          slot-scope="{props}"
        >
          <td :class="props.className">
            <div class="btn-group">
              <router-link
                :to="`/videopostprocessing/theme/${ themeId }/userdependentimageelement/${ props.dataItem.id }`"
                class="btn btn-primary btn-sm"
              >
                <font-awesome-icon
                  class="mr-2"
                  icon="edit"
                />
                <span>Edit</span>
              </router-link>
              <button
                class="btn btn-secondary btn-sm"
                @click="openConfirmRemoveModal(props.dataItem.id)"
              >
                <font-awesome-icon
                  class="mr-2 gray"
                  icon="trash"
                />
                <span>Remove</span>
              </button>
            </div>
          </td>
        </template>
      </Grid>
    </template>
    
    <div class="mt-4">
      <router-link
        :to="`/videopostprocessing/theme`"
        class="btn btn-sm btn-secondary float-left"
      >
        <font-awesome-icon
          class="mr-2 gray"
          icon="caret-left"
        />{{ $t('back') }}
      </router-link>
      <button
        class="btn btn-sm btn-primary float-right"
        @click="addUserDependentImageElement()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="plus"
        />{{ $t('add') }}
      </button>
      <div class="clearfix" />
    </div>

    <SweetModal
      ref="confirmDelete"
      icon="warning"
      blocking
      title="Delete user dependent image?"
      class="overflowHidden"
    >
      <p>{{ $t('userDependentImageListComp.sureToDelete') }}</p>
      <button
        slot="button"
        class="btn btn-secondary float-left mb-3"
        @click="$refs.confirmDelete.close()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="times"
        />{{ $t('cancel') }}
      </button>
      <button
        slot="button"
        class="btn btn-danger float-right mb-3"
        @click="removeUserDependentImageElement()"
      >
        <font-awesome-icon
          class="mr-2"
          icon="trash"
        />{{ $t('delete') }}
      </button>
      <div class="clearfix" />
    </SweetModal>
    <Sidebar
      v-if="showSidebar"
      :show-sidebar="showSidebar"
      @close="close"
    >
      <ThemeElementAdd
        :theme-id="themeId"
        element-type="UserDependentImageElement"
        @reloadThemeElements="closeAndReloadData"
      />
    </Sidebar>
  </Portlet>
</template>

<script>
import { SweetModal } from 'sweet-modal-vue';
import { kendoGridMixin } from '@/mixins/kendoGridMixin.js';

export default {
  name: "UserDependentImageList",
  components: {
    Preview: () => import('@/components/VideoPostprocessing/Preview.vue'),
    SweetModal,
    ThemeElementTabs: () => import('@/components/VideoPostprocessing/Theme/ThemeElementTabs.vue'),
    Sidebar: () => import('@/components/Base/Sidebar.vue'),
    ThemeElementAdd: () => import('@/components/VideoPostprocessing/Theme/ThemeElementAdd.vue'),
  },
  mixins: [
    kendoGridMixin
  ],
  props: {
    themeId: {
      type: String,
      required: true
    }
  },
  data () {
    return {
      theme: null,
      userDependentImageElements: null,
      showSidebar: false,
      loading: true,
      indexToDelete: null,
      kgm_filter: {
        logic: "and",
        filters: [
          {
            field: 'name',
            operator: 'contains',
            value: ''
          }
        ]
      },
      kgm_sort: [
        {
          "field": "name",
          "dir": "asc"
        }
      ],
      kgm_columns: [
        {
          filterable: false,
          title: 'Preview',
          width: '90px',
          cell: 'previewTemplate'
        },
        {
          filterable: true,
          filter: 'text',
          title: 'Source Name',
          field: 'name',
          filterCell: "nameFilter"
        },
        {
          filterable: false,
          title: 'Options',
          cell: 'optionsTemplate',
          width: '200px'
        }
      ]
    }
  },
  computed: {
    portletTitle () {
      if (this.theme == null) {
        return this.$t('userDependentImageListComp.theme');
      }
      if (this.theme.name == null) {
        return this.$t('userDependentImageListComp.theme')
      }
      return `${ this.$t('userDependentImageListComp.theme') }: ${ this.theme.name }`
    }
  },
  async created () {
    await this.getTheme();
    this.getUserDependentImageElements();
  },
  methods: {
    //#region API-calls
   addUserDependentImageElement () {
      this.showSidebar = true;
    },
    closeAndReloadData () {
      this.showSidebar = false;    
      this.$emit("reloadAuditLogs");
      this.getUserDependentImageElements();
    },
    close () {
      this.showSidebar = false;
    },
    reloadElements () {
      this.getUserDependentImageElements();
      this.show();
    },
    openConfirmRemoveModal (index) {
      this.indexToDelete = index;
      this.$refs.confirmDelete.open();
    },
    removeUserDependentImageElement () {
      this.axios.delete('/VideoPostProcessing/DeleteUserDependentImageElement?userDependentImageElementId=' + this.indexToDelete)
        .then(() => {
          this.$emit("reloadAuditLogs");
          this.getUserDependentImageElements();
          this.$snotify.success(this.$t('userDependentImageListComp.successfullyDeleted'));
        })
        .finally(() => {
          this.indexToDelete = null;
          this.$refs.confirmDelete.close();
        });
    },
    getUserDependentImageElements () {
      this.axios.get('/VideoPostProcessing/GetAllUserDependentImageElements?themeId=' + this.themeId)
        .then((response) => {
          if (response == null) {
            return;
          }
          if (response.data == null) {
            return;
          }
          this.userDependentImageElements = response.data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getTheme ()
    {
      await this.axios.get(`/VideoPostProcessing/GetTheme?themeId=${ this.themeId }`)
        .then((response) => {
          this.theme = response.data;
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    }
    //#endregion
  }
}
</script>
<style>
.userDependentImageList .k-grid table tr:hover td {
  background :rgb(219, 219, 219) !important;
  cursor: pointer !important;
}
</style>